import { Button, Card, ToggleButton, ToggleButtonGroup } from '@mui/material';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import type { ManagementSummaryParameters, ManagementSummaryReportFormat } from '@parkchirp/reporting';
import type { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import { FacilityAutocomplete } from '../inputs';
import { reportingPanelStyles } from './styles';
import type { ReportFormProps } from './types';

type ManagementSummaryDateRangeValue = {
	startDate: Dayjs | null;
	endDate: Dayjs | null;
};

const initialDateRange: ManagementSummaryDateRangeValue = {
	startDate: null,
	endDate: null,
};

export const ManagementSummaryReportForm = ({
	createReport,
	isGeneratingReport,
	organizationId,
	onValidationError,
}: ReportFormProps) => {
	const [dateRange, setDateRange] = useState<ManagementSummaryDateRangeValue>(initialDateRange);
	const [facilityId, setFacilityId] = useState<string | null>(null);
	const [format, setFormat] = useState<ManagementSummaryReportFormat>('xlsx');

	const canSubmit = useMemo(() => {
		return Boolean(dateRange.startDate && dateRange.endDate && facilityId);
	}, [dateRange.endDate, dateRange.startDate, facilityId]);

	const reset = () => {
		setDateRange(initialDateRange);
		setFacilityId(null);
		setFormat('xlsx');
	};

	const buildParameters = (): ManagementSummaryParameters => {
		if (!dateRange.startDate || !dateRange.endDate) {
			throw new Error('Select a start and end date.');
		}

		if (!facilityId) {
			throw new Error('Select a facility.');
		}

		return {
			startDate: dateRange.startDate.format('YYYY-MM-DD'),
			endDate: dateRange.endDate.format('YYYY-MM-DD'),
			facilityId,
			format,
		};
	};

	const validate = () => {
		if (!dateRange.startDate) {
			onValidationError('Select a start date.');
			return false;
		}

		if (!dateRange.endDate) {
			onValidationError('Select an end date.');
			return false;
		}

		if (dateRange.endDate.isBefore(dateRange.startDate, 'day')) {
			onValidationError('End date must be on or after the start date.');
			return false;
		}

		if (!facilityId) {
			onValidationError('Select a facility.');
			return false;
		}

		return true;
	};

	const submit = async () => {
		if (!validate()) {
			return;
		}

		await createReport({
			reportType: 'managementSummary',
			parameters: buildParameters(),
		});
	};

	return (
		<Card
			variant='outlined'
			sx={(theme) => ({
				...reportingPanelStyles(theme),
				overflow: 'hidden',
				p: 3,
			})}
		>
			<Stack spacing={2.5}>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<Stack spacing={2}>
						<Typography variant='subtitle1' fontWeight={600}>
							Date range
						</Typography>
						<Typography variant='subtitle2' fontWeight={400}>
							Start and end dates are required
						</Typography>

						<Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
							<DatePicker<Dayjs>
								label='Start date'
								value={dateRange.startDate}
								maxDate={dateRange.endDate ?? undefined}
								onChange={(startDate) => {
									setDateRange({
										startDate,
										endDate: dateRange.endDate,
									});
								}}
								slotProps={{ textField: { fullWidth: true } }}
							/>

							<DatePicker<Dayjs>
								label='End date'
								value={dateRange.endDate}
								minDate={dateRange.startDate ?? undefined}
								onChange={(endDate) => {
									setDateRange({
										startDate: dateRange.startDate,
										endDate,
									});
								}}
								slotProps={{ textField: { fullWidth: true } }}
							/>
						</Stack>
					</Stack>
				</LocalizationProvider>

				<Divider />

				<Stack spacing={1.5}>
					<Typography variant='subtitle1' fontWeight={600}>
						Facility
					</Typography>
					<Typography variant='subtitle2' fontWeight={400}>
						Search and select one facility.
					</Typography>
					<FacilityAutocomplete
						label='Facility'
						organizationId={organizationId}
						required
						onChange={(facility) => {
							setFacilityId(facility?.id ?? null);
						}}
						isForReports
					/>
				</Stack>

				<Divider />

				<Stack spacing={1.5}>
					<Typography variant='subtitle1' fontWeight={600}>
						Output
					</Typography>

					<ToggleButtonGroup
						exclusive
						size='small'
						value={format}
						onChange={(_, nextFormat: ManagementSummaryReportFormat | null) => {
							if (nextFormat) {
								setFormat(nextFormat);
							}
						}}
					>
						<ToggleButton value='xlsx'>XLSX</ToggleButton>
						<ToggleButton value='pdf'>PDF</ToggleButton>
					</ToggleButtonGroup>
				</Stack>

				<Divider />

				<Stack
					direction='row'
					justifyContent='flex-end'
					spacing={2}
					sx={{
						borderColor: 'divider',
						borderTop: 1,
						pt: 2,
					}}
				>
					<Button onClick={reset} disabled={isGeneratingReport}>
						Reset
					</Button>

					<Button onClick={submit} disabled={!canSubmit || isGeneratingReport}>
						Generate Report
					</Button>
				</Stack>
			</Stack>
		</Card>
	);
};
